import { generateError, getFirstDayCurrentMonthAsText, getTodayAsText } from './';
import {
    checkAvailableMaxDateTodayAsText,
    checkAvailableMinDateText,
    formatDateForApi,
    getTextDateFormat,
    isValidTextDate,
} from './dateCalc';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { appConfig } from '../settings';
import { IFilterParams, IParamsList, SelectType } from '../interfaces';

/**
 * Drops unit types the tenant may not see, so a bookmarked or stale `filter[original_unit_type]`
 * cannot select a hidden type. Falls back to the first visible type on pages that need a default.
 * `visibleUnitTypes` is passed in rather than read from the store to keep this layer store-free.
 */
export const queryParamsSanitizeUnitTypes = (
    params: IParamsList,
    filterParams: IFilterParams | undefined,
    visibleUnitTypes: string[],
): IParamsList => {
    if (!filterParams?.unitTypes) {
        return params;
    }
    const newParams = { ...params };
    const requested = Array.isArray(newParams.unitTypes) ? newParams.unitTypes : [newParams.unitTypes];
    const allowed = requested.filter((i): i is string => !!i && visibleUnitTypes.includes(i));
    const isMultiple = filterParams.unitTypes === SelectType.MULTIPLE;

    if (allowed.length) {
        newParams.unitTypes = isMultiple ? allowed : allowed[0];
    } else if (filterParams.defaultUnitTypes && visibleUnitTypes.length) {
        newParams.unitTypes = isMultiple ? [visibleUnitTypes[0]] : visibleUnitTypes[0];
    } else {
        delete newParams.unitTypes;
    }

    return newParams;
};

export const getDefaultDateRate = (filterParams?: IFilterParams): IParamsList => ({
    endDate:
        filterParams?.defaultDateRangeType === 'month' || filterParams?.defaultDateRangeType === 'day'
            ? getTodayAsText()
            : undefined,
    startDate:
        filterParams?.defaultDateRangeType === 'day'
            ? getTodayAsText()
            : filterParams?.defaultDateRangeType === 'month'
            ? getFirstDayCurrentMonthAsText()
            : undefined,
});
export const queryParamsNormalize = (params: IParamsList, filterParams?: IFilterParams): IParamsList => {
    const newParams = { ...params };
    if (
        newParams.pageSize
        // && typeof newParams.pageSize === 'string'
    ) {
        newParams.pageSize = +newParams.pageSize;
    }
    if (
        newParams.pageNumber
        // && typeof newParams.pageNumber === 'string'
    ) {
        newParams.pageNumber = +newParams.pageNumber;
    }

    if (newParams.startDate && !isValidTextDate(newParams.startDate)) {
        delete newParams.startDate;
    }
    if (newParams.endDate && !isValidTextDate(newParams.endDate)) {
        delete newParams.endDate;
    }

    if (
        !newParams.endDate &&
        (filterParams?.isDateRangeMaxDayToDay ||
            filterParams?.defaultDateRangeType === 'month' ||
            filterParams?.defaultDateRangeType === 'day')
    ) {
        newParams.endDate = getTodayAsText();
    }

    if (filterParams?.isDateRangeMaxDayToDay && newParams.endDate) {
        newParams.endDate = checkAvailableMaxDateTodayAsText(newParams.endDate);
    }

    if (!newParams.startDate) {
        switch (filterParams?.defaultDateRangeType) {
            case 'month':
                newParams.startDate = getFirstDayCurrentMonthAsText();
                break;
            case 'day':
                newParams.startDate = getTodayAsText();
                break;
            default:
                filterParams?.defaultDateRangeType && generateError(filterParams.defaultDateRangeType);
                break;
        }
    }

    if (newParams.startDate && newParams.endDate && new Date(newParams.startDate) > new Date(newParams.endDate)) {
        const tmp = dayjs(newParams.startDate).format(getTextDateFormat(newParams.startDate));
        newParams.startDate = dayjs(newParams.endDate).format(getTextDateFormat(newParams.endDate));
        newParams.endDate = tmp;
    }

    if (newParams.startDate && filterParams?.isDateRangeMinDayOn) {
        newParams.startDate = checkAvailableMinDateText(newParams.startDate);
    }

    if (filterParams?.defaultUnitTypes && !newParams.unitTypes) {
        newParams.unitTypes = filterParams.defaultUnitTypes;
    }
    return newParams;
};

export const queryParamsCleanDefault = (params: IParamsList, filterParams?: IFilterParams): IParamsList => {
    const cleanedParams = { ...params };
    if (cleanedParams.pageNumber && cleanedParams.pageNumber === appConfig.pageDefaultParams.pageNumber) {
        delete cleanedParams.pageNumber;
    }
    if (cleanedParams.pageSize && cleanedParams.pageSize === appConfig.pageDefaultParams.pageSize) {
        delete cleanedParams.pageSize;
    }
    if (
        cleanedParams.endDate &&
        cleanedParams.endDate === appConfig.pageDefaultParams.today &&
        (filterParams?.defaultDateRangeType === 'month' || filterParams?.defaultDateRangeType === 'day')
    ) {
        delete cleanedParams.endDate;
    }
    if (
        cleanedParams.startDate &&
        ((cleanedParams.startDate === appConfig.pageDefaultParams.startThisMonth &&
            filterParams?.defaultDateRangeType === 'month') ||
            (cleanedParams.startDate === appConfig.pageDefaultParams.today &&
                filterParams?.defaultDateRangeType === 'day'))
    ) {
        delete cleanedParams.startDate;
    }
    return cleanedParams;
};

const filterParamsToFilterArray = (filterParams?: IFilterParams): string[] =>
    filterParams
        ? Object.entries(filterParams)
              .filter(([, value]) => value)
              .map(([key]) => {
                  return key;
              })
        : [];
const isKeyOnFilterParams = (key: string, filter: string[]): boolean =>
    !filter.length ||
    (filter.includes(key) && key !== 'dateRange') ||
    ((key === 'startDate' || key === 'endDate') && filter.includes('dateRange')) ||
    ((key === 'month' || key === 'year') && filter.includes('dateMonth'));

export const apiParamsCleaner = (params: IParamsList, filterParams?: IFilterParams): IParamsList => {
    const filter = filterParamsToFilterArray(filterParams);
    return Object.fromEntries(
        Object.entries(params)
            .filter((a) => a[1])
            .filter((a) => {
                if (Array.isArray(a[1]) && !a[1].length) {
                    return false;
                }
                return a;
            })
            // .map((i) => {
            //     const [key, value] = i;
            //     const isKey = isKeyOnFilterParams(key, filter);
            //     console.log(
            //         new Date().toISOString(),
            //         '-(isKey)->',
            //         isKey,
            //         `-key->`,
            //         key,
            //         `-value->`,
            //         typeof value,
            //         value,
            //     );
            //     return i;
            // })
            .filter(([key]) => isKeyOnFilterParams(key, filter))
            .map(([key, value]) => [key, value]),
    );
};

export const urlParamsCleaner = (params: IParamsList, filterParams?: IFilterParams): IParamsList => {
    const filter = filterParamsToFilterArray(filterParams);

    return Object.fromEntries(
        Object.entries(params)
            .filter((a) => a[1])
            .filter((a) => a[0] !== 'pageNumber' || a[1] !== appConfig.pageDefaultParams.pageNumber)
            .filter((a) => a[0] !== 'pageSize' || a[1] !== appConfig.pageDefaultParams.pageSize)
            .filter(([key]) => isKeyOnFilterParams(key, filter))
            .map(([key, value]) => [key, value]),
    );
};

export const formatParamsToUrl = (
    query: string,
    value: string | number | true | string[] | number[],
    options?: object,
): string => {
    let str = '';
    if (Array.isArray(value)) {
        str = queryString.stringify({ [query]: value }, { ...options });
    } else {
        const valEncode = encodeURIComponent(value);
        str = `${query}=${valEncode}`;
    }

    return str;
};

export const params2Url = (params: IParamsList): string => {
    let string = '';
    Object.entries(params).forEach(([key, value]) => {
        let str = '';
        if (value) {
            if (typeof value === 'object' && !Array.isArray(value)) {
                console.error('--(bad params2Url)-key ->', key, '-value ->', { ...value });
            } else {
                switch (key) {
                    case 'prev':
                    case 'search':
                        str = formatParamsToUrl(key, value);
                        break;
                    case 'pageNumber':
                        str = formatParamsToUrl('page[number]', value);
                        break;
                    case 'pageSize':
                        str = formatParamsToUrl('page[size]', value);
                        break;
                    case 'paginate':
                        str = formatParamsToUrl('paginate', value);
                        break;
                    case 'read':
                        str = formatParamsToUrl('filter[read]', value);
                        break;
                    case 'startDate':
                        str = formatParamsToUrl('filter[start_date]', formatDateForApi(value as string));
                        break;
                    case 'endDate':
                        str = formatParamsToUrl('filter[end_date]', formatDateForApi(value as string));
                        break;
                    case 'fromDate':
                        str = formatParamsToUrl('filter[from_date]', formatDateForApi(value as string));
                        break;
                    case 'day':
                        str = formatParamsToUrl('filter[day]', value);
                        break;
                    case 'month':
                        str = formatParamsToUrl('filter[month]', value);
                        break;
                    case 'year':
                        str = formatParamsToUrl('filter[year]', value);
                        break;
                    case 'toDate':
                        str = formatParamsToUrl('filter[to_date]', formatDateForApi(value as string));
                        break;
                    case 'companyId':
                        str = formatParamsToUrl('filter[company_id]', value);
                        break;
                    case 'assignedCompanyId':
                        str = formatParamsToUrl('filter[assigned_company_id]', value);
                        break;
                    case 'buyerId':
                        str = formatParamsToUrl('filter[buyer_id]', value);
                        break;
                    case 'packageId':
                        str = formatParamsToUrl('filter[package_id]', value);
                        break;
                    case 'ids':
                        str = formatParamsToUrl('filter[ids]', value, {
                            arrayFormat: 'bracket',
                            encode: false,
                        });
                        break;
                    case 'countryId':
                        str = formatParamsToUrl('filter[country_id]', value);
                        break;
                    case 'cardId':
                        str = formatParamsToUrl('filter[card_id]', value);
                        break;
                    case 'cardSetId':
                        str = formatParamsToUrl('filter[card_set_id]', value);
                        break;
                    case 'cardSetsId':
                        str = formatParamsToUrl('filter[card_set_id]', value, {
                            arrayFormat: 'bracket',
                            encode: false,
                        });
                        break;
                    case 'exportType':
                        str = formatParamsToUrl('export_type', value);
                        break;
                    case 'entityType':
                        str = formatParamsToUrl('entity_type', value);
                        break;
                    case 'parentId':
                        str = formatParamsToUrl('node_id', value);
                        break;
                    case 'stream':
                        str = formatParamsToUrl('stream', true);
                        break;
                    case 'sessionId':
                        str = formatParamsToUrl('filter[session_id]', value);
                        break;
                    case 'operatorId':
                        str = formatParamsToUrl('filter[operator_id]', value);
                        break;
                    case 'transactionId':
                        str = formatParamsToUrl('filter[transaction_id]', value);
                        break;
                    case 'unitType':
                        str = formatParamsToUrl('filter[original_unit_type]', value);
                        break;
                    case 'unitTypes':
                        str = formatParamsToUrl('filter[original_unit_type]', value, {
                            arrayFormat: 'bracket',
                            encode: false,
                        });
                        break;
                    // case 'unitTypeIds':
                    //     console.warn(
                    //         '--(params2Url)-> unitTypeIds should be array of id or string id, but value ->',
                    //         value,
                    //     );
                    //     const queryUnitTypesId = 'filter[original_unit_type_id]';
                    //     let parsedUnitTypeValueId;
                    //
                    //     if (Array.isArray(value)) {
                    //         parsedUnitTypeValueId = value;
                    //     } else {
                    //         const unitTypeValue = getUnitTypesSelectById(value as string)?.value;
                    //         parsedUnitTypeValueId = String(unitTypeValue);
                    //     }
                    //
                    //     str = formatParamsToUrl(queryUnitTypesId, parsedUnitTypeValueId, {
                    //         arrayFormat: 'bracket',
                    //         encode: false,
                    //     });
                    //     break;
                    case 'fileId':
                        str = formatParamsToUrl('filter[file_id]', value);
                        break;
                    case 'importType':
                        str = formatParamsToUrl('filter[type]', value);
                        break;
                    case 'status':
                    case 'importStatus':
                    case 'subStatus':
                    case 'uploadFileStatusId':
                        str = formatParamsToUrl('filter[status]', value);
                        break;
                    case 'gatewayId':
                        str = formatParamsToUrl('filter[gateway]', value);
                        break;
                    case 'commandId':
                        str = formatParamsToUrl('filter[operation_name]', value);
                        break;
                    case 'userId':
                        str = formatParamsToUrl('filter[user_id]', value);
                        break;
                    case 'entityId':
                        str = formatParamsToUrl('filter[entity]', value);
                        break;
                    case 'networkId':
                        str = formatParamsToUrl('filter[network_id]', value);
                        break;
                    case 'zoneId':
                        str = formatParamsToUrl('filter[zone_id]', value);
                        break;
                    case 'isActiveThisMonth':
                        str = formatParamsToUrl('filter[active_this_month]', value);
                        break;
                    case 'isEimProvided':
                        str = formatParamsToUrl('filter[eim_provided]', value);
                        break;
                    case 'isDelete':
                        if (value) {
                            str = formatParamsToUrl('scope[]', 'only_deleted');
                        }
                        break;
                    case 'withoutMno':
                        if (value) {
                            str = formatParamsToUrl('scope[]', 'without_mno');
                        }
                        break;
                    case 'isActivityLogUsers':
                        if (value) {
                            str = formatParamsToUrl('scope[]', 'with_activity_logs');
                        }
                        break;
                    case 'withDeleted':
                        if (value) {
                            str = formatParamsToUrl('scope[]', 'with_deleted');
                        }
                        break;
                    case 'usageScheduled':
                        if (value) {
                            str = formatParamsToUrl('filter[scheduled]', value);
                        }
                        break;
                    case 'paymentType':
                        str = formatParamsToUrl('filter[payment_type]', value);
                        break;
                    case 'companyTypeId':
                        str = formatParamsToUrl('filter[company_type_id]', value);
                        break;
                    case 'fromMonth':
                        str = formatParamsToUrl('filter[from_month]', value);
                        break;
                    case 'tillMonth':
                        str = formatParamsToUrl('filter[till_month]', value);
                        break;
                    case 'invoiceDate':
                        str = formatParamsToUrl('date', value);
                        break;
                    case 'companyInvoice':
                        str = formatParamsToUrl('filter[invoicing]', value);
                        break;
                    case 'invoiceable':
                        str = formatParamsToUrl('scope[]', 'invoiceable');
                        break;
                    case 'byInvoicing':
                        str = formatParamsToUrl('by_invoicing', value);
                        break;
                    case 'sellerId':
                        str = formatParamsToUrl('seller_id', value);
                        break;
                    case 'mfa_token':
                        str = formatParamsToUrl('mfa_token', value);
                        break;
                    case 'boughtForMyCompany':
                        str = formatParamsToUrl('bought_for_my_company', value);
                        break;
                    case 'packageType':
                        str = formatParamsToUrl('filter[package_type]', value);
                        break;
                    case 'tariffCountryIds':
                        str = formatParamsToUrl('filter[country_id]', value, {
                            arrayFormat: 'bracket',
                            encode: false,
                        });
                        break;
                    case 'srlNetworksCompanyId':
                        str = formatParamsToUrl('filter[srl_networks_company_id]', value);
                        break;
                    case 'cardsSubsCompanyId':
                        str = formatParamsToUrl('filter[cards_subs_company_id]', value);
                        break;
                    case 'tariffId':
                        str = formatParamsToUrl('filter[tariff_id]', value, {
                            arrayFormat: 'bracket',
                            encode: false,
                        });
                        break;
                    case 'tariffPackageId':
                        str = formatParamsToUrl('filter[tariff_package_id]', value, {
                            arrayFormat: 'bracket',
                            encode: false,
                        });
                        break;
                    default:
                        str = '';
                }
                if (str) {
                    string = `${string ? string + '&' : '?'}${str}`;
                }
            }
        }
    });
    return string;
};

export const removePageOptions = (options: string): string => {
    return options
        .split('&')
        .filter((o) => !o.includes('page'))
        .join('&');
};
